import type { SiteContent } from './content-types.js';
import { DEFAULT_LOCALE, type Locale } from './locales.js';

/**
 * Page content by language, held in a registry rather than imported eagerly.
 *
 * The four dictionaries are about 160 kB of prose between them, and a reader needs
 * exactly one. Importing all four from here would put all four in the browser bundle, so
 * instead each is registered by whoever loads it: the prerenderer registers all of them
 * (it renders every language), and the browser dynamically imports only the language it
 * is displaying.
 *
 * `getContent` throws rather than falling back to English on a missing language. A
 * silent fallback would hydrate an English React tree over French prerendered markup,
 * which React resolves by throwing the correct page away and re-rendering it in the
 * wrong language. Failing loudly at the one call site that could cause it is safer.
 */

const REGISTRY = new Map<Locale, SiteContent>();

export function registerContent(locale: Locale, content: SiteContent): void {
  REGISTRY.set(locale, content);
}

export function isContentLoaded(locale: Locale): boolean {
  return REGISTRY.has(locale);
}

export function getContent(locale: Locale = DEFAULT_LOCALE): SiteContent {
  const content = REGISTRY.get(locale);
  if (!content) {
    throw new Error(`Content for "${locale}" has not been registered.`);
  }
  return content;
}
